import React, { useState, useEffect, memo, useMemo } from 'react';
import { GlobalTracker } from '@/utils/Tracker';
import { feishuRobotSend } from '@/utils/feishuRobot';
import { isEmpty } from 'lodash';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import styles from './PageServerError.module.less';
import { LocalLang, ThirdLinkConfig } from './PageServerError.config';

/**
 * 500页面异常
 * @param info 体现在上报消息中
 */
const PageServerError = memo((props) => {
  const LANG = useMemo(() => {
    if (typeof window !== 'undefined') {
      return LocalLang[window.country?.language?.toLowerCase() ?? 'en_us'];
    }
    return {};
  }, []);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(LANG['error.500.others.description']);

  const [thirdLink, setThirdLink] = useState();
  const [thirdLinkTitle, setThirdLinkTitle] = useState();

  const links = {
    amazon: {
      US: 'https://www.amazon.com/insta360',
      DE: 'https://www.amazon.de/insta360',
      ES: 'https://www.amazon.es/insta360',
      FR: 'https://www.amazon.fr/insta360',
      IT: 'https://www.amazon.it/insta360',
      GB: 'https://www.amazon.co.uk/insta360',
      JP: 'https://www.amazon.co.jp/insta360',
      content: LANG['error.500.amazon.description'],
      btn: LANG['error.500.amazon.redirectBtn'],
      title: LANG['error.500.amazon.title'],
    },
    aliexpress: {
      common: 'https://www.aliexpress.com/store/1101718277',
      content: LANG['error.500.aliExpress.description'],
      btn: LANG['error.500.aliExpress.redirectBtn'],
      title: LANG['error.500.aliExpress.title'],
    },
    lazada: {
      MY: 'https://www.lazada.com.my/shop/insta360-store/?spm=a2o4k.pdp_revamp.seller.1.4a5441aaSl1fpq&itemId=2877739282&channelSource=pdp',
      PH: 'https://www.lazada.com.ph/shop/insta360-store/?spm=a2o4l.pdp_revamp.seller.1.283bef2drTL5lH&itemId=2186825021&channelSource=pdp',
      SG: 'https://www.lazada.sg/shop/insta360-store/?path=index.htm&spm=a2o42.pdp_revamp.seller.1.eb215b3fbts5kb&itemId=2245266313&channelSource=pdp',
      TH: 'https://www.lazada.co.th/shop/insta360-store/?spm=a2o4m.pdp_revamp.seller.1.574f5ab7bMHu5g&itemId=3575337882&channelSource=pdp',
      VN: 'https://www.lazada.vn/shop/insta360-store/?path=index.htm&spm=a2o4n.pdp_revamp.seller.1.6a7b62daDaGa2V&itemId=1782123052&channelSource=pdp',
      content: LANG['error.500.lazada.description'],
      btn: LANG['error.500.lazada.redirectBtn'],
      title: LANG['error.500.lazada.title'],
    },
    tmall: {
      CN: 'https://insta360.tmall.com/',
      content: LANG['error.500.tmall.description'],
      btn: LANG['error.500.tmall.redirectBtn'],
      title: LANG['error.500.tmall.title'],
    },
  };

  const handleTracker = (type) => {
    const { code, X_Language } = window.country || {};
    if (type === 'link') {
      const countryConfig = ThirdLinkConfig[code];
      GlobalTracker.ErrorPage({
        action: 'click_thirdPlatform_link',
        eventLabel: `${code}_${X_Language}`,
        eventValue: countryConfig?.store ?? '-',
      });
    }
    if (type === 'visible') {
      GlobalTracker.ErrorPage({
        action: 'show_500_page',
        eventLabel: `${code}_${X_Language}`,
        eventValue: window.location.href,
      });
    }
  };

  const getNextData = () => {
    try {
      const _cloneData = JSON.parse(JSON.stringify(window.__NEXT_DATA__ || '')); // JSON.parse(JSON.stringify())速度比lodash快很多
      if (_cloneData?.props?.pageData?.runtimeInject) {
        _cloneData.props.pageData.runtimeInject.localeData = isEmpty(
          _cloneData.props.pageData.runtimeInject.localeData
        );
      }
      return _cloneData;
    } catch (e) {
      feishuRobotSend({
        text: ['pageServerError getNextData catch error'],
        at: ['dxy'],
        err: e,
      });
      return window.__NEXT_DATA__;
    }
  };

  useEffect(() => {
    const { code, X_Language } = window.country || {};
    const countryConfig = ThirdLinkConfig[code];

    feishuRobotSend({
      text: [
        '【系统异常页面】 pageServerError',
        `【页面info】: ${props.info ?? ''}`,
        `【__NEXT_DATA__】: ${JSON.stringify(getNextData() || '')}`,
      ],
      at: ['dxy'],
    });

    armsErrorSend('系统异常页面', {
      filename: 'PageErrorBoundary Catch 系统异常页面',
      data: { info: props.info, nextData: getNextData() },
    });

    // 跳转三方网站配置
    if (countryConfig) {
      const curPlatform = links[countryConfig.store];
      setThirdLink(curPlatform[countryConfig.area]);
      setThirdLinkTitle(curPlatform.btn);
      setDescription(curPlatform.content);
      setTitle(curPlatform.title ?? '');
    }
    handleTracker('visible');
  }, []);

  return (
    <div className={styles.PageServerError}>
      <div className={styles.container}>
        <img
          className={styles.img}
          src='https://res.insta360.com/static/314004f04f108aa0558b9ef9eeaee829/4x.png'
          alt=''
        />
        {!props.hideBtn && (
          <div className={styles.content}>
            <h2>{title}</h2>
            <div className={styles.text} dangerouslySetInnerHTML={{ __html: description ?? '' }} />
            <div className={styles.btnGroup}>
              {thirdLink && thirdLinkTitle && (
                <a href={thirdLink} onClick={() => handleTracker('link')}>
                  {thirdLinkTitle}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default PageServerError;
